// services/firebaseService.js
import {
  db,
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
} from "../auth/firebase"; // adjust the path if needed

export const createDocument = async (collectionName, data, docId) => {
  try {
    if (docId) {
      await setDoc(doc(db, collectionName, docId), {
        ...data,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      return docId;
    } else {
      const docRef = await addDoc(collection(db, collectionName), {
        ...data,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      return docRef.id;
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Update document in collection
 */
export const updateDocument = async (collectionName, docId, data) => {
  try {
    const docRef = doc(db, collectionName, docId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: new Date(),
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Delete document from collection
 */
export const deleteDocument = async (collectionName, docId) => {
  try {
    const docRef = doc(db, collectionName, docId);
    await deleteDoc(docRef);
  } catch (error) {
    throw error;
  }
};

/**
 * Get a single document
 */
export const getDocument = async (collectionName, docId) => {
  try {
    const docRef = doc(db, collectionName, docId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all documents in a collection
 */
export const getCollection = async (collectionName) => {
  try {
    const snapshot = await getDocs(collection(db, collectionName));
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    throw error;
  }
};

/* ---------------- New: Query Collection with search + pagination ---------------- */
export const getCollectionQuery = async ({
  collectionName,
  limit: limitCount = 10,
  startAfter: startAfterDoc = null,
  orderBy: orderField = "createdAt",
  search = null,
}) => {
  if (!collectionName) {
    return { data: [], lastDoc: null };
  }

  try {
    const colRef = collection(db, collectionName);
    const constraints = [];

    // Search
    if (search && search.fields?.length && search.value) {
      // Firestore cannot natively do OR across multiple fields.
      // For simplicity, we'll only use the first field for query equality.
      constraints.push(where(search.fields[0], "==", search.value));
    }

    // Ordering
    constraints.push(orderBy(orderField, "desc"));

    // Pagination
    constraints.push(limit(limitCount));
    if (startAfterDoc) constraints.push(startAfter(startAfterDoc));

    const q = query(colRef, ...constraints);
    const snapshot = await getDocs(q);

    return {
      data: snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
    };
  } catch (err) {
    return { data: [], lastDoc: null };
  }
};
